import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { CorpSegmentNames } from 'src/constants/corp-segment-constants';
import { getQuarterFromMonth } from 'src/utils/date-time-utilities';
import { ColumnGroupType } from '../types/ColumnTypes';

/**
 * Converts an array of month identifiers to their corresponding quarters.
 * This function processes a list of month-year strings and extracts the unique quarters they belong to.
 *
 * @param {string[]} monthsOfThisYear - An array of month strings formatted as "MonthYear" (e.g., "Jan2023").
 * @returns {string[]} An array of unique quarter identifiers derived from the input months, formatted as "Q1", "Q2", "Q3", or "Q4".
 */
export const getQuartersFromMonths = (monthsOfThisYear: string[]): string[] => {
  const quarters: Set<string> = new Set();
  monthsOfThisYear?.forEach((month) => {
    quarters.add(getQuarterFromMonth(month));
  });
  return Array.from(quarters);
};

/**
 * To check the pinned state based on group and segment name
 * @param group The column group type
 * @param segmentName The segment name
 * @returns The pinned state or undefined
 */
export const isColumnPinned = (group: ColumnGroupType, segmentName: CorpSegmentNames): 'left' | 'right' | undefined => {
  if (group === 'ClosedGroup' && segmentName === CorpSegmentNames.COST_CENTER) {
    return 'left';
  }
  return undefined;
};

/**
 * Helper function to update row data and refresh cells
 * @param node The grid node
 * @param rowData The row data
 * @param fieldName The field name to update
 * @param newValue The new value
 */
export const updateRowData = (node: any, rowData: any, fieldName: string, newValue: string | null) => {
  rowData[fieldName] = newValue;
  node?.setDataValue(fieldName, newValue);
  node?.gridOptionsWrapper?.gridOptions?.api?.refreshCells({ force: true });
};

/**
 * Sums up numeric values from specified columns in a row of data.
 * If there are no numeric values or the data is absent, returns null.
 * This function considers zero as a valid number contributing to the sum.
 *
 * @param {ValueGetterParams} params - Parameters containing the row data.
 * @param {ColDef[]} columnDefinitions - Definitions of columns to sum up.
 * @returns {number | null} The sum of the column values, or null if no valid numbers are found.
 */
export const sumUpColumns = (params: ValueGetterParams, columnDefinitions: ColDef<any, any>[]): number | null => {
  try {
    // Check if params.data is available
    if (!params.data) {
      return null;
    }

    let sum = 0;
    let containsNumberOrZero = false; // Flag to track if any number or zero has been added to the sum.

    columnDefinitions.forEach((column: any) => {
      if (column.field && !column.field.toLowerCase().endsWith('total')) {
        const value = params.data[column.field];

        // Only process values that are not null or undefined
        if (value !== null && value !== undefined) {
          if (value === '0' || !isNaN(Number(value))) {
            containsNumberOrZero = true;
            sum += Number(value); // Convert to number to ensure correct addition
          }
        }
      }
    });

    // Return sum if any valid number or zero is present, otherwise return null
    return containsNumberOrZero ? sum : null;
  } catch (error) {
    return null;
  }
};

/**
 * Calculates the sum of all numeric values from columns within each quarter that do not end in 'total'.
 * This function is designed to aggregate numeric data across multiple months within a year,
 * skipping any aggregated total columns.
 * It returns null if no numeric data is found; otherwise, it returns the sum.
 *
 * @param {ValueGetterParams} params - Contains data for the current row in the grid.
 * @param {ColDef[]} quarterWithMonthlyHeaderInfo - An array of column definitions, each representing a quarter, containing child columns for each month.
 * @returns {number|null} The sum of all numeric monthly values within the year, or null if there are no numbers.
 */
export const sumUpYearlyColumns = (params: ValueGetterParams, quarterWithMonthlyHeaderInfo: ColDef[]): number | null => {
  try {
    let sum = 0;
    let containsNumberOrZero = false; // Flag to track if any number or zero has been added to the sum.

    quarterWithMonthlyHeaderInfo.forEach((quarterMonth: any) => {
      quarterMonth.children?.forEach((monthly: any) => {
        if (monthly.field && !monthly.field.toLowerCase().endsWith('total')) {
          // Skip 'total' columns
          const value = params.data[monthly.field];
          if (value !== null && value !== undefined) {
            if (value === '0' || !isNaN(Number(value))) {
              containsNumberOrZero = true;
              sum += Number(value); // Convert to Number to ensure correct arithmetic operation.
            }
          }
        }
      });
    });

    return containsNumberOrZero ? sum : null; // Return the calculated sum or null if no valid numbers were processed.
  } catch (error: any) {
    return null;
  }
};

/**
 * Creates rich select cell editor parameters
 * @param values The values for the dropdown
 * @returns The rich cell editor parameters
 */
export const createRichCellEditorParams = (values: string[]) => ({
  values: values,
  valueListMaxHeight: 300,
  allowTyping: true,
  searchType: 'matchAny',
  filterList: true,
  highlightMatch: true,
  valueListMaxWidth: 400,
  cellRenderer: (params: any) => {
    return params.value;
  },
  formatValue: (value: any) => value
});
